import { captureMessage } from './sentryLoader';

// Track reported audio URLs to avoid sending duplicate warnings per session
const reportedAudioUrls = new Set();

/** Send a "broken audio" warning to Sentry, deduped per URL per session. */
export function reportAudioUnavailable(audioUrl, errorMessage) {
  if (!audioUrl || audioUrl === 'unknown') return;
  if (reportedAudioUrls.has(audioUrl)) return;
  reportedAudioUrls.add(audioUrl);
  captureMessage('Audio source not available', 'warning', {
    audioUrl,
    errorMessage,
  });
}

/**
 * Safely play audio/video, silencing expected errors.
 *
 * Handles:
 * - AbortError: User navigates away, clicks something else, component unmounts
 * - NotSupportedError: Audio source missing/404/unsupported format
 * - iOS volume reduction after recording (audio session mode issue)
 *
 * These are not actionable errors for users and should not crash the app.
 */
export function safePlay(mediaElement) {
  if (!mediaElement) return Promise.resolve(false);

  // Ensure volume is at max (can be reduced by iOS audio session mode changes after recording)
  // Only set for audio elements, not video (which may have intentional volume control)
  // NOTE: We intentionally do NOT set navigator.audioSession.type here because setting it
  // to 'playback' would block subsequent microphone access for recording.
  if (mediaElement.tagName === 'AUDIO') {
    mediaElement.volume = 1.0;
  }

  const playPromise = mediaElement.play();

  if (playPromise !== undefined) {
    return playPromise.then(() => true).catch((err) => {
      // AbortError is expected when playback is interrupted - silence it
      if (err.name === 'AbortError') {
        return false;
      }

      // NotSupportedError means the audio source is missing/404/unsupported.
      if (err.name === 'NotSupportedError') {
        const audioUrl = mediaElement.src || mediaElement.currentSrc || 'unknown';
        reportAudioUnavailable(audioUrl, err.message);
        return false;
      }

      // Re-throw other errors (NotAllowedError, etc.)
      throw err;
    });
  }

  return Promise.resolve(true);
}
